import { useEffect, useState } from "react";

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// The site pages' motion layer. Content is visible without it: `.rv` blocks
// only start hidden once the root has the `js` class, which is never added
// when the visitor prefers reduced motion or IntersectionObserver is missing.
// Blocks rendered later (loaded events, another season, search hits) are
// picked up as they mount.
export const useSiteMotion = (rootRef) => {
  const [animate] = useState(() => !reducedMotion() && typeof window !== "undefined" && "IntersectionObserver" in window);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !animate) return undefined;
    const hero = root.querySelector(".hero");
    const frame = requestAnimationFrame(() => {
      if (hero) hero.classList.add("in");
      root.querySelectorAll(".hero .rv").forEach((el, i) => {
        el.style.setProperty("--i", i + 2);
        el.classList.add("in");
      });
    });
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("in");
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: "0px 0px -10% 0px" });
    const watch = () => root.querySelectorAll(".rv:not(.in)").forEach((el) => {
      if (el.closest(".hero .hero-head")) return;
      io.observe(el);
    });
    watch();
    const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(watch) : null;
    if (mo) mo.observe(root, { childList: true, subtree: true });
    return () => { cancelAnimationFrame(frame); io.disconnect(); if (mo) mo.disconnect(); };
  }, [rootRef, animate]);

  // Cursor spotlight on `.glow` cards.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const move = (e) => {
      const g = e.target.closest && e.target.closest(".glow");
      if (!g) return;
      const r = g.getBoundingClientRect();
      g.style.setProperty("--mx", `${e.clientX - r.left}px`);
      g.style.setProperty("--my", `${e.clientY - r.top}px`);
    };
    root.addEventListener("pointermove", move, { passive: true });
    return () => root.removeEventListener("pointermove", move);
  }, [rootRef]);

  return animate;
};

// Page title and description for a public page, put back as they were when
// the visitor moves on into the app. The canonical link follows the route on
// its own (Components/CanonicalLink.js).
export const usePageMeta = (title, description) => {
  useEffect(() => {
    const prevTitle = document.title;
    const undo = [];
    const set = (selector, tag, attrs, attr, value) => {
      let el = document.head.querySelector(selector);
      if (el) {
        const prev = el.getAttribute(attr);
        undo.push(() => el.setAttribute(attr, prev));
      } else {
        el = document.createElement(tag);
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
        undo.push(() => el.remove());
      }
      el.setAttribute(attr, value);
    };
    document.title = title;
    set('meta[name="description"]', "meta", { name: "description" }, "content", description);
    return () => {
      document.title = prevTitle;
      undo.forEach((fn) => fn());
    };
  }, [title, description]);
};

// Scrolls to the URL's #section once it exists (`ready` turns true after
// late-loading content), for links arriving from another page such as
// /organisers#pricing. Repeat clicks on the page itself go through
// session.js scrollIfSamePage.
export const useHashScroll = (location, ready = true) => {
  const { hash, key } = location;
  useEffect(() => {
    if (!ready || !hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (el) el.scrollIntoView({ block: "start" });
  }, [hash, key, ready]);
};
